import { CircleAlert, CircleCheck, LoaderCircle, RefreshCw, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { controller } from "../app/controller.ts";
import { useApp } from "../state/store.ts";
import { Wizard } from "./onboarding/Wizard.tsx";
import { Tour } from "./onboarding/Tour.tsx";
import { useOnboarding } from "../lib/onboarding.ts";
import { TopBar } from "./TopBar.tsx";
import { Toolbar } from "./Toolbar.tsx";
import { PlansPanel } from "./PlansPanel.tsx";
import { Inspector } from "./Inspector.tsx";
import { StatusBar } from "./StatusBar.tsx";
import { HelpOverlay, ToolHint } from "./Help.tsx";
import { AdRail } from "./AdSlot.tsx";
import { BoardPanel } from "./Board.tsx";
import { LayoutPanel } from "./LayoutPanel.tsx";
import { SchematicsView } from "./schematics/SchematicsView.tsx";
import { SCHEMATIC_EXTENSIONS } from "@mcp/world";
import { SignInDialog } from "./Account.tsx";
import { BillingReturnDialog, DesktopCheckoutDialog, LiveWorldsDialog, PlansDialog } from "./Plans.tsx";
import { Banners } from "./Banners.tsx";
import { CollabActivity, CollabPanel } from "./Share.tsx";
import { initAuth, useAccount } from "../lib/auth.ts";
import { handleBillingReturn, initBilling, openPlans, parseBillingReturn, parsePlansLink, plansLinkReason } from "../lib/billing.ts";
import { UpdateDialog } from "./UpdateDialog.tsx";
import { FeedbackDialog } from "./Feedback.tsx";
import { AcademyLink } from "./Academy.tsx";
import { initUpdater } from "../lib/updater.ts";

export function App() {
  const viewport = useRef<HTMLDivElement>(null);
  const phase = useApp((s) => s.phase);
  const progress = useApp((s) => s.progress);
  const error = useApp((s) => s.error);
  const errorHelp = useApp((s) => s.errorHelp);
  const notice = useApp((s) => s.notice);
  const set = useApp((s) => s.set);
  const wizardOpen = useOnboarding((s) => phase !== "ready" || s.holding || s.leaving);
  const sideCollapsed = useApp((s) => s.sideCollapsed);
  const collabOpen = useApp((s) => s.collabOpen);

  useEffect(() => {
    controller.mount(viewport.current!);
    void controller.restoreCachedAssets();
    void controller.showBackdrop(); // the demo island behind the setup wizard (in the game's textures, if kept here)
    void controller.checkInvite();
    // nothing happens (no network) unless this browser has signed in before; back from Stripe: switch the plan on
    void initAuth().then(() => {
      if (parseBillingReturn()) void handleBillingReturn(useApp.getState().collab.url);
      // `#plans` (e.g. "Start 7 days free" in the Minecraft mod): the Plans dialog
      const plansLink = parsePlansLink();
      if (plansLink) {
        // signed out: keep the address, so signing in with Discord/Google/Microsoft (which comes back to it) opens the
        // dialog again; signed in: it has done its job
        if (useAccount.getState().status === "signed-in") history.replaceState(null, "", location.pathname + location.search);
        openPlans(plansLinkReason(plansLink.ref));
      }
      initBilling(() => useApp.getState().collab.url); // plan banners stay fresh; desktop: mcplanner://billing/… links
    });
    void initUpdater(); // desktop app: "New update available" (does nothing in the browser)
    // an invite link restored after signing in with Discord/Google/Microsoft (lib/auth.ts), or pasted into the address bar
    const onHash = () => { if (useApp.getState().phase === "setup") void controller.checkInvite(); };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const ready = phase === "ready";
  return (
    <div className={`app phase-${phase}${ready ? " shell" : ""}${ready && sideCollapsed ? " side-collapsed" : ""}`}>
      {ready && <TopBar />}
      {ready && <PlansPanel />}
      {/* the map card: the viewport element stays the same across phases (the engine is mounted into it once) */}
      <main
        className="stage viewport-wrap"
        onDragOver={(e) => { if (phase === "ready" && e.dataTransfer.types.includes("Files")) e.preventDefault(); }}
        onDrop={(e) => {
          if (phase !== "ready") return;
          // a schematic dropped on the map: import it (then name it / add its link / place it)
          const files = [...e.dataTransfer.files].filter((f) => SCHEMATIC_EXTENSIONS.some((x) => f.name.toLowerCase().endsWith(x)));
          if (!files.length) return;
          e.preventDefault();
          void (async () => { for (const f of files) await controller.importAndEdit(f); })();
        }}
      >
        <div className="viewport" ref={viewport} />
        {ready && (
          <>
            <div className="stage-vignette" aria-hidden="true" />
            <Toolbar />
            <ToolHint />
            <WorldChangedBanner />
            <Banners />
            <Inspector />
            <LayoutPanel />
            <BoardPanel />
            {collabOpen && <CollabPanel onClose={() => set({ collabOpen: false })} />}
            <CollabActivity />
            <StatusBar />
            <Tour />
          </>
        )}
      </main>
      {ready && <AdRail />}
      {ready && <HelpOverlay />}
      {ready && <SchematicsView />}
      <Wizard />
      <FeedbackDialog />
      <SignInDialog />
      <PlansDialog />
      <LiveWorldsDialog />
      <DesktopCheckoutDialog />
      <BillingReturnDialog />
      <UpdateDialog />
      {progress && !wizardOpen && (
        <div className="toast progress" role="status">
          <LoaderCircle className="spin" size={16} aria-hidden="true" />
          <div className="grow">
            <div className="toast-text">{progress.msg}</div>
            <div className="meter"><i style={{ width: `${Math.round(progress.frac * 100)}%` }} /></div>
          </div>
          <span className="toast-pct tabular">{Math.round(progress.frac * 100)}%</span>
        </div>
      )}
      {notice && (
        <div className="toast notice" role="status">
          <CircleCheck size={16} aria-hidden="true" />
          <span className="toast-text">{notice.text}</span>
          {notice.path && <button className="btn btn-sm btn-outline" onClick={() => controller.revealPath(notice.path!)}>Show in folder</button>}
          <button className="btn btn-ghost btn-icon btn-sm" onClick={() => set({ notice: null })} title="Dismiss" aria-label="Dismiss"><X size={15} aria-hidden="true" /></button>
        </div>
      )}
      {error && (
        <div className="toast error" role="alert" onClick={() => set({ error: null, errorHelp: null })} title="Click to dismiss">
          <CircleAlert size={16} aria-hidden="true" />
          <span className="toast-text">
            {error}{" "}
            {errorHelp?.message === error && <AcademyLink to={errorHelp.key} about="this problem">Troubleshooting</AcademyLink>}
          </span>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={(e) => { e.stopPropagation(); set({ error: null, errorHelp: null }); }} title="Dismiss" aria-label="Dismiss"><X size={15} aria-hidden="true" /></button>
        </div>
      )}
    </div>
  );
}

/** Desktop: Minecraft saved the open world — offer to re-read it. */
function WorldChangedBanner() {
  const changed = useApp((s) => s.worldChanged);
  const [busy, setBusy] = useState(false);
  if (!changed) return null;
  return (
    <div className="world-changed glass" role="status">
      <RefreshCw size={15} aria-hidden="true" className="wc-icon" />
      <span>Minecraft saved changes to this world.</span>
      <button className="btn btn-sm btn-primary" disabled={busy} onClick={async () => { setBusy(true); await controller.reloadLocalWorld(); setBusy(false); }}>{busy ? <><LoaderCircle className="spin" size={14} aria-hidden="true" />Reloading…</> : "Reload map"}</button>
      <button className="btn btn-sm btn-ghost" onClick={() => useApp.getState().set({ worldChanged: false })} title="Ignore until the next save">Later</button>
    </div>
  );
}
